import { type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatedDialog } from "./AnimatedDialog";
import { Button } from "./Button";
import { cn } from "@/lib/utils";

export type ConfirmDialogTone = "info" | "warning" | "danger";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Caller is responsible for closing on confirm; useful for async actions. */
  onConfirm: () => void;
  tone?: ConfirmDialogTone;
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  tone = "info",
  confirmDisabled = false,
}: ConfirmDialogProps) {
  return (
    <AnimatedDialog open={open} onOpenChange={onOpenChange} maxWidth="max-w-md">
      <div className="p-6">
        <Dialog.Title
          className={cn(
            "text-base font-semibold",
            tone === "danger"
              ? "text-red-700"
              : tone === "warning"
                ? "text-amber-700"
                : "text-[var(--color-text-primary)]"
          )}
        >
          {title}
        </Dialog.Title>
        <Dialog.Description asChild>
          <div className="mt-2 text-sm text-[var(--color-text-secondary)] leading-relaxed">
            {description}
          </div>
        </Dialog.Description>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </AnimatedDialog>
  );
}
